import config from 'constants/config'
import getPaymentMethods from './getPaymentMethods'
import { getPaymentMethodsByRegion } from './getPaymentMethods'
import {
  STRIPE_3DS_PAYMENT_TYPE, STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE, STRIPE_PAYMENT_ELEMENT_METHODS,
} from 'constants/payment'
import { dateDifference, beginningOfToday } from 'lib/dateUtils'
import { cartIncludesFlights, tripType } from 'lib/flights'
import {
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  STRIPE_PAYMENT_ELEMENT_APPLE_PAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_GOOGLE_PAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE,
} from 'constants/payment'
export interface AccommodationMetaDataObject {
  deal_country: string;
  lead_time?: number;
  deal_destination: string;
  is_refundable?: string;
}

export const paymentElementMethodMap = {
  [STRIPE_PAYMENT_ELEMENT_CARD_TYPE]: 'card',
  [STRIPE_PAYMENT_ELEMENT_APPLE_PAY_TYPE]: 'apple_pay',
  [STRIPE_PAYMENT_ELEMENT_GOOGLE_PAY_TYPE]: 'google_pay',
  [STRIPE_PAYMENT_ELEMENT_AFTERPAY_TYPE]: 'afterpay_clearpay',
  [STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE]: 'giropay',
}

export const getPaymentElementRedirectionURL = (orderId): string => { return `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/checkout/purchase?orderId=${orderId}&` }

export const isStripe3dsEnabled = (currency: string): boolean => !!(config.STRIPE_3DS_ENABLED && (true || getPaymentMethods(currency).includes(STRIPE_3DS_PAYMENT_TYPE)))

export const isStripePaymentElementCardEnabled = (regionCode: string): boolean => !!regionCode && !!(config.STRIPE_PAYMENT_ELEMENT_ENABLED) && getPaymentMethodsByRegion(regionCode).includes(STRIPE_PAYMENT_ELEMENT_CARD_TYPE)

export const isStripePaymentElementGiropayEnabled = (regionCode: string): boolean => !!regionCode && !!config.STRIPE_PAYMENT_ELEMENT_GIROPAY_ENABLED && getPaymentMethodsByRegion(regionCode).includes(STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE)

export const getStripePaymentElementMethods = (regionCode: string): Array<string> => regionCode ? (getPaymentMethodsByRegion(regionCode).filter(method => STRIPE_PAYMENT_ELEMENT_METHODS.includes(method))) : []

export const isStripe3dsForStandaloneEnabled = (): boolean => !!config.STRIPE_3DS_STANDALONE_FLIGHTS_ENABLED

export const isStripe3dsForGiftCardsEnabled = (): boolean => !!config.STRIPE_3DS_GIFT_CARDS_ENABLED

export const isStripeWalletFlightsEnabled = (): boolean => !!config.STRIPE_WALLET_FLIGHTS_ENABLED

export const isStripeBedbankRepeatCustomerCheckEnabled = (): boolean => !!config.STRIPE_BEDBANK_REPEAT_CUSTOMER_CHECK_ENABLED

export const prepareAdditionalInfoForStripe = (cart: App.CartState, account: App.AuthAccount, utm: App.UTMState, currencyCode: string) => {
  try {
    const { accommodation, bedbank, giftCard } = cart
    const commonMetaData = {
      spoofed_customer: !!account.isSpoofed,

      utm_source: utm.source,
      utm_medium: utm.medium,

      is_bnbl: cart.accommodation.offer && accommodation.reservationType != 'instant_booking',

      order_currency: currencyCode,
    }

    return {
      ...commonMetaData,
      ...(accommodationMetaData(accommodation) || bedBankMetaData(bedbank)),
      ...giftCardMetaData(giftCard, account),
      ...flightsMetaData(cart),
      ...standaloneExperiencesMetaData(cart),
      product_type: productType(cart),
    }
  } catch (err) {
    return {}
  }
}

export const prepareInstalmentAdditionalInfoForStripe = (order: App.Order) => {
  try {
    return {
      // Null asserting here, since the bad dereference is being handled by try catch
      product_type: order.instalmentDetails!.items[0].offerType,
    }
  } catch (err) {
    return {}
  }
}

const accommodationMetaData = (accommodation: App.CartAccommodation | undefined) : AccommodationMetaDataObject | undefined => {
  if (!accommodation) return undefined

  const metaData: AccommodationMetaDataObject = {
    deal_country: accommodation.offer.property?.geoData?.countryCode || '',
    deal_destination: accommodation.offer.property?.geoData?.administrativeAreaLevel1 || '',
  }

  if (accommodation.startDate) {
    metaData.lead_time = dateDifference(new Date(accommodation.startDate), beginningOfToday()).days
  }

  return metaData
}

const bedBankMetaData = (bedbankItem: App.CartBedbank) => {
  if (bedbankItem.price > 0) {
    return {
      lead_time: dateDifference(new Date(bedbankItem.checkIn), beginningOfToday()).days,
      deal_country: bedbankItem.offer.property?.address?.countryCode,
      deal_destination: bedbankItem.offer.property?.address?.city,
      is_refundable: bedbankItem.rate?.refundable,
    }
  }
}

const giftCardMetaData = (giftCard: App.CartGiftCard, account: App.AuthAccount) => {
  if (giftCard.price > 0) {
    return {
      gift_card_recipient_name: giftCard.personalisation.to || account.fullName,
      gift_card_recipient_email: giftCard.personalisation.email_of_recipient || account.email,
    }
  }
}

const flightsMetaData = (cart: App.CartState) => {
  if (cartIncludesFlights(cart)) {
    return {
      lead_time: dateDifference(new Date(cart.flight.journey.departing.flights[0].departingDate), beginningOfToday()).days,
      trip_type: tripType(cart.flight.journey),
      departure_city: cart.flight.journey.departing.flights[0].departingAirport,
      arrival_city: cart.flight.journey.returning?.flights[0].departingAirport,
      departure_country: cart.flight.journey.departing.flights[0].departingCountry,
      arrival_country: cart.flight.journey.departing.flights[0].arrivalCountry,
    }
  }
}

const standaloneExperiencesMetaData = (cart: App.CartState) => {
  if (isStandaloneExperience(cart)) {
    const experienceItems = cart.experiences.items
    const itemId = Object.keys(experienceItems)[0]
    const item = experienceItems[itemId]
    const experience = item.experience
    if (experience) {
      return {
        lead_time: item.date ? dateDifference(new Date(item.date), beginningOfToday()).days : 0,
        product_subtype: experience.primaryCategory?.name,
        deal_destination: experience.location.name,
      }
    }
  }
}

const isInsuranceTransaction = (cart: App.CartState) => {
  const offerItem = cart.items.find(e => e.type === 'offer')
  if (offerItem && offerItem.status === 'completed' && cart.insurance.insuranceAccepted) {
    return true
  }
  return false
}

const isStandaloneExperience = (cart: App.CartState) => {
  return Object.keys(cart.experiences.items).length != 0
}

const productType = (cart: App.CartState) => {
  if (cart.giftCard.price > 0) {
    return 'gift_card'
  }
  if (isInsuranceTransaction(cart)) {
    return 'insurance'
  }
  if (cart.accommodation.offer) {
    return cart.accommodation.offer.type
  }
  if (cart.bedbank.price > 0) {
    return cart.bedbank.offer.type
  }
  if (cartIncludesFlights(cart)) {
    return 'flight'
  }
  if (isStandaloneExperience(cart)) {
    return 'experience'
  }
}
